/**
 * One seller, in full — eligibility, every settlement row, every payout
 * request, and the actions that move a payout along.
 *
 * This used to be the only way to see anything on this page. It is kept, and
 * demoted: the table above answers "who is owed what" for everyone, and this
 * is where an operator goes once they know which seller they are investigating.
 * The approve / reject / release actions live here because they act on an
 * individual payout request, which is a thing this view lists and the
 * aggregate table above deliberately does not.
 *
 * The operator verifies the seller's account here, then pays an approved
 * payout either through RazorpayX (the transfer is sent and its UTR arrives by
 * webhook) or by hand from the platform's bank, recording the UTR.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import type { PayoutRequest, Settlement } from '@terra/api-client';
import { Input, Spinner } from '@terra/ui';

import { api } from '../../lib/api';
import { formatPaise } from '../../lib/money';

import { StagePill } from './StagePill';
import { PAYOUT_TONE, SETTLEMENT_TONE } from './tones';

export function SellerLookup({
  /** Set when a row in the table above was opened, so the two stay in step. */
  vendorId: controlledVendorId,
}: {
  vendorId?: string | undefined;
}) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState('');
  const [activeVendor, setActiveVendor] = useState('');
  const [open, setOpen] = useState(false);

  const vendorId = controlledVendorId ?? activeVendor;

  const eligibility = useQuery({
    queryKey: ['admin-vendor-eligibility', vendorId],
    queryFn: () => api.settlements.adminEligibility(vendorId),
    enabled: !!vendorId,
  });
  const vendorSettlements = useQuery<Settlement[]>({
    queryKey: ['admin-vendor-settlements', vendorId],
    queryFn: () => api.settlements.adminSettlements(vendorId),
    enabled: !!vendorId,
  });
  const vendorPayouts = useQuery<PayoutRequest[]>({
    queryKey: ['admin-vendor-payouts', vendorId],
    queryFn: () => api.settlements.adminPayouts(vendorId),
    enabled: !!vendorId,
  });

  const approvePayout = useMutation({
    mutationFn: (id: string) => api.settlements.approvePayout(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-vendor-payouts'] }),
  });
  const rejectPayout = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.settlements.rejectPayout(id, reason),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-vendor-payouts'] }),
  });
  const releasePayout = useMutation({
    mutationFn: ({ id, bankRef }: { id: string; bankRef?: string }) =>
      api.settlements.releasePayout(
        id,
        bankRef ? { provider: 'MANUAL', bankRef } : { provider: 'RAZORPAY_X' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-vendor-payouts'] });
      void qc.invalidateQueries({ queryKey: ['admin-vendor-eligibility'] });
    },
  });
  const bankAccounts = useQuery({
    queryKey: ['admin-vendor-bank-accounts', vendorId],
    queryFn: () => api.settlements.adminBankAccounts(vendorId),
    enabled: !!vendorId,
  });
  const verifyBank = useMutation({
    mutationFn: (accountId: string) => api.settlements.verifyBankAccount(accountId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-vendor-bank-accounts'] }),
  });
  const settleNow = useMutation({
    mutationFn: (subOrderId: string) => api.settlements.settleSubOrder(subOrderId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-vendor-settlements'] });
      void qc.invalidateQueries({ queryKey: ['admin-vendor-eligibility'] });
    },
  });
  const accountById = new Map<string, NonNullable<typeof bankAccounts.data>[number]>(
    (bankAccounts.data ?? []).map((a) => [a.id, a]),
  );
  const actionError = [releasePayout, approvePayout, rejectPayout, verifyBank, settleNow].find(
    (m) => m.isError,
  )?.error;

  const expanded = open || Boolean(vendorId);

  return (
    <section
      aria-label="Seller lookup"
      className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={expanded}
        className="hover:bg-surface-subtle/40 flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors"
      >
        <div>
          <h2 className="text-ink text-[14px] font-bold">Seller investigation</h2>
          <p className="text-ink-muted mt-0.5 text-[12.5px] font-medium">
            Every settlement row and payout request for one seller, and the approve / reject /
            release actions.
          </p>
        </div>
        <ChevronDown
          className={`text-ink-muted h-4 w-4 shrink-0 transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>

      {expanded && (
        <div className="border-border/60 border-t">
          <div className="border-border/60 border-b px-5 py-4">
            <form
              className="flex flex-wrap items-center gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                setActiveVendor(typed.trim());
              }}
            >
              <Input
                placeholder="Seller id (cuid)"
                aria-label="Seller id"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="h-10 w-full rounded-xl font-mono text-[13px] sm:w-96"
              />
              <button
                type="submit"
                className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all"
              >
                Load seller
              </button>
              {controlledVendorId && (
                <span className="text-ink-muted text-[12px] font-semibold">
                  Showing the seller opened from the table above.
                </span>
              )}
            </form>
          </div>

          {vendorId && (
            <div className="space-y-8 p-5">
              {actionError && (
                <p
                  role="alert"
                  className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] font-semibold text-red-700"
                >
                  {actionError.message}
                </p>
              )}
              <div>
                <h3 className="text-ink mb-4 text-[14px] font-bold">Eligibility</h3>
                {eligibility.isLoading ? (
                  <Spinner size="sm" />
                ) : eligibility.data ? (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    {[
                      { label: 'Receivable', value: eligibility.data.receivablePaise },
                      { label: 'Held in reserve', value: eligibility.data.heldPaise },
                      { label: 'Withdrawable', value: eligibility.data.withdrawablePaise },
                      { label: 'Payout threshold', value: eligibility.data.payoutThresholdPaise },
                    ].map((m) => (
                      <div
                        key={m.label}
                        className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4"
                      >
                        <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                          {m.label}
                        </p>
                        <p className="text-ink mt-1 text-[16px] font-bold tabular-nums">
                          {formatPaise(m.value)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-ink-muted text-[13px] font-medium">
                    Could not load eligibility for this seller.
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-ink mb-1 text-[14px] font-bold">Bank accounts</h3>
                <p className="text-ink-muted mb-4 text-[12.5px] font-medium">
                  A seller can only be paid into a verified account. Check the number and IFSC
                  against their cancelled cheque or bank letter before verifying.
                </p>
                {bankAccounts.isLoading ? (
                  <Spinner size="sm" />
                ) : bankAccounts.isError ? (
                  <p className="text-ink-muted text-[13px] font-medium">
                    Could not load bank accounts for this seller.
                  </p>
                ) : (bankAccounts.data ?? []).length === 0 ? (
                  <p className="text-ink-muted text-[13px] font-medium">
                    This seller has not added a bank account yet, so they cannot be paid.
                  </p>
                ) : (
                  <div className="border-border/60 bg-surface overflow-hidden rounded-xl border shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                            <th className="px-5 py-4">Holder</th>
                            <th className="px-5 py-4">Account number</th>
                            <th className="px-5 py-4">IFSC</th>
                            <th className="px-5 py-4 text-center">Status</th>
                            <th className="px-5 py-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-border/40 divide-y text-[13px]">
                          {(bankAccounts.data ?? []).map((a) => (
                            <tr key={a.id}>
                              <td className="text-ink px-5 py-4 font-semibold">
                                {a.accountHolderName}
                                {a.isDefault && (
                                  <span className="text-ink-muted ml-2 text-[11px] font-bold uppercase">
                                    Default
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-4 font-mono text-xs">{a.accountNumber}</td>
                              <td className="px-5 py-4 font-mono text-xs">{a.ifsc}</td>
                              <td className="px-5 py-4 text-center">
                                <StagePill
                                  tone={
                                    a.status === 'VERIFIED'
                                      ? 'emerald'
                                      : a.status === 'PENDING'
                                        ? 'amber'
                                        : 'slate'
                                  }
                                >
                                  {a.status}
                                </StagePill>
                              </td>
                              <td className="px-5 py-4">
                                <div className="flex justify-end">
                                  {a.status === 'PENDING' && (
                                    <button
                                      type="button"
                                      disabled={verifyBank.isPending}
                                      className="bg-ink hover:bg-ink/90 flex h-8 items-center justify-center rounded-lg px-3 text-[12px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
                                      onClick={() => {
                                        if (
                                          confirm(
                                            `Verify ${a.accountHolderName}, ${a.accountNumber}, ${a.ifsc}? Payouts will be sent to this account.`,
                                          )
                                        )
                                          verifyBank.mutate(a.id);
                                      }}
                                    >
                                      Verify
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-ink mb-4 text-[14px] font-bold">Settlements</h3>
                {vendorSettlements.isLoading ? (
                  <Spinner size="sm" />
                ) : (vendorSettlements.data ?? []).length === 0 ? (
                  <p className="text-ink-muted text-[13px] font-medium">No settlements found.</p>
                ) : (
                  <div className="border-border/60 bg-surface overflow-hidden rounded-xl border shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                            <th className="px-5 py-4">Sub-order</th>
                            <th className="px-5 py-4 text-right">Gross</th>
                            <th className="px-5 py-4 text-right">Commission</th>
                            <th className="px-5 py-4 text-right">Reserve held</th>
                            <th className="px-5 py-4 text-right">Net</th>
                            <th className="px-5 py-4 text-center">Status</th>
                            <th className="px-5 py-4 text-right">Eligible at</th>
                            <th className="px-5 py-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-border/40 divide-y text-[13px]">
                          {(vendorSettlements.data ?? []).map((s) => (
                            <tr key={s.id} className="hover:bg-surface-subtle/30 transition-colors">
                              <td className="px-5 py-4 font-mono text-xs">
                                {s.subOrderId.slice(0, 10)}…
                              </td>
                              <td className="px-5 py-4 text-right tabular-nums">
                                {formatPaise(s.grossPaise)}
                              </td>
                              <td className="px-5 py-4 text-right tabular-nums">
                                {formatPaise(s.commissionPaise)}
                              </td>
                              <td className="px-5 py-4 text-right tabular-nums">
                                {formatPaise(s.reserveHeldPaise)}
                              </td>
                              <td className="text-ink px-5 py-4 text-right font-bold tabular-nums">
                                {formatPaise(s.netPayablePaise)}
                              </td>
                              <td className="px-5 py-4 text-center">
                                <StagePill tone={SETTLEMENT_TONE[s.status] ?? 'slate'}>
                                  {s.status.replace(/_/g, ' ')}
                                </StagePill>
                              </td>
                              <td className="text-ink-subtle px-5 py-4 text-right text-xs">
                                {s.eligibleAt ? new Date(s.eligibleAt).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-5 py-4">
                                <div className="flex justify-end">
                                  {s.status === 'ELIGIBLE' && (
                                    <button
                                      type="button"
                                      disabled={settleNow.isPending}
                                      className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-8 items-center justify-center rounded-lg border px-3 text-[12px] font-bold shadow-sm transition-all disabled:opacity-50"
                                      onClick={() => settleNow.mutate(s.subOrderId)}
                                    >
                                      Settle now
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-ink mb-4 text-[14px] font-bold">Payout requests</h3>
                {vendorPayouts.isLoading ? (
                  <Spinner size="sm" />
                ) : (vendorPayouts.data ?? []).length === 0 ? (
                  <p className="text-ink-muted text-[13px] font-medium">No payouts requested.</p>
                ) : (
                  <div className="border-border/60 bg-surface overflow-hidden rounded-xl border shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                            <th className="px-5 py-4">Requested</th>
                            <th className="px-5 py-4 text-right">Amount</th>
                            <th className="px-5 py-4 text-center">Status</th>
                            <th className="px-5 py-4">Pay to</th>
                            <th className="px-5 py-4">Bank ref</th>
                            <th className="px-5 py-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-border/40 divide-y text-[13px]">
                          {(vendorPayouts.data ?? []).map((p) => (
                            <tr key={p.id} className="hover:bg-surface-subtle/30 transition-colors">
                              <td className="text-ink-subtle px-5 py-4 text-xs">
                                {new Date(p.requestedAt).toLocaleString()}
                              </td>
                              <td className="text-ink px-5 py-4 text-right font-bold tabular-nums">
                                {formatPaise(p.amountPaise)}
                              </td>
                              <td className="px-5 py-4 text-center">
                                <StagePill tone={PAYOUT_TONE[p.status] ?? 'slate'}>
                                  {p.status}
                                </StagePill>
                              </td>
                              <td className="px-5 py-4 text-xs">
                                {(() => {
                                  const acct = p.bankAccountId
                                    ? accountById.get(p.bankAccountId)
                                    : undefined;
                                  return acct ? (
                                    <span className="font-mono">
                                      {acct.accountNumber} · {acct.ifsc}
                                    </span>
                                  ) : (
                                    '—'
                                  );
                                })()}
                              </td>
                              <td className="px-5 py-4 font-mono text-xs">{p.bankRef ?? '—'}</td>
                              <td className="px-5 py-4">
                                <div className="flex items-center justify-end gap-2">
                                  {p.status === 'PENDING' && (
                                    <>
                                      <button
                                        type="button"
                                        className="bg-ink hover:bg-ink/90 flex h-8 items-center justify-center rounded-lg px-3 text-[12px] font-bold text-white shadow-sm transition-all"
                                        onClick={() => approvePayout.mutate(p.id)}
                                      >
                                        Approve
                                      </button>
                                      <button
                                        type="button"
                                        className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-8 items-center justify-center rounded-lg border px-3 text-[12px] font-bold shadow-sm transition-all"
                                        onClick={() => {
                                          const reason = prompt('Rejection reason?');
                                          if (reason && reason.length >= 4)
                                            rejectPayout.mutate({ id: p.id, reason });
                                        }}
                                      >
                                        Reject
                                      </button>
                                    </>
                                  )}
                                  {p.status === 'APPROVED' && (
                                    <>
                                      <button
                                        type="button"
                                        className="flex h-8 items-center justify-center rounded-lg bg-emerald-600 px-3 text-[12px] font-bold text-white shadow-sm transition-all hover:bg-emerald-700 disabled:opacity-50"
                                        disabled={releasePayout.isPending}
                                        onClick={() => {
                                          if (
                                            confirm(
                                              `Send ${formatPaise(p.amountPaise)} to the seller's bank through RazorpayX now?`,
                                            )
                                          )
                                            releasePayout.mutate({ id: p.id });
                                        }}
                                      >
                                        Pay via RazorpayX
                                      </button>
                                      <button
                                        type="button"
                                        className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-8 items-center justify-center rounded-lg border px-3 text-[12px] font-bold shadow-sm transition-all disabled:opacity-50"
                                        disabled={releasePayout.isPending}
                                        onClick={() => {
                                          const bankRef = prompt(
                                            'Transfer the money first, then enter the bank reference (UTR) of that transfer:',
                                          )?.trim();
                                          if (bankRef && bankRef.length >= 4)
                                            releasePayout.mutate({ id: p.id, bankRef });
                                        }}
                                      >
                                        Paid manually
                                      </button>
                                    </>
                                  )}
                                  {p.status === 'RELEASED' && p.providerStatus && (
                                    <span className="text-ink-muted text-[12px] font-semibold">
                                      RazorpayX: {p.providerStatus}
                                    </span>
                                  )}
                                  {p.status === 'FAILED' && p.failureReason && (
                                    <span
                                      className="max-w-[16rem] truncate text-[12px] font-semibold text-red-700"
                                      title={p.failureReason}
                                    >
                                      {p.failureReason} (amount returned to balance)
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
